import React from 'react';
import { Box, Card, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatMoney } from '../../utils/configUtils';

const COLORS = {
  revenue: '#065F46',
  fee: '#0D9488',
  sales: '#6B5344',
};

/* ─── Custom tooltip ──────────────────────────────────────────────── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const entries = [
    { key: 'Gross Revenue', color: COLORS.revenue },
    { key: 'Platform Fee', color: COLORS.fee },
    { key: 'Units Sold', color: COLORS.sales },
  ];

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #f0f0f0',
      borderRadius: 16,
      padding: '12px 16px',
      boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
      fontFamily: 'Heebo, sans-serif',
      minWidth: 190,
    }}>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      {payload.map((entry) => {
        const meta = entries.find((e) => e.key === entry.name);
        const isUnits = entry.name === 'Units Sold';
        return (
          <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: meta?.color ?? entry.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>{entry.name}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
              {isUnits ? entry.value.toLocaleString() : formatMoney(Math.round(entry.value * 100))}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Custom legend ───────────────────────────────────────────────── */
const ChartLegend = ({ payload }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, paddingTop: 14, flexWrap: 'wrap' }}>
    {payload?.map((entry) => (
      <div key={entry.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Heebo', fontSize: 12, color: '#6b7280' }}>
        <div style={{ width: 20, height: 3, borderRadius: 4, background: entry.color }} />
        {entry.value}
      </div>
    ))}
  </div>
);

/* ─── Chart ───────────────────────────────────────────────────────── */
const RevenueChart = ({ data, loading, title = 'Revenue Overview' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartHeight = isMobile ? 220 : 300;

  if (loading) {
    return (
      <Card sx={{ borderRadius: '24px', boxShadow: '3px 2px 20px 0px rgba(0,0,0,0.04)', p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width={200} height={32} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width={120} height={20} sx={{ mb: 2.5 }} />
        <Skeleton variant="rectangular" height={chartHeight} sx={{ borderRadius: '16px' }} />
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: '24px', boxShadow: '3px 2px 20px 0px rgba(0,0,0,0.04)', p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: { xs: 15, sm: 18 }, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Heebo', lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#9ca3af', fontFamily: 'Heebo', mt: 0.25 }}>
          Revenue, platform fee & units over time
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={data ?? []} margin={{ top: 10, right: isMobile ? 8 : 16, bottom: 0, left: isMobile ? -8 : 0 }}>
          <defs>
            <linearGradient id="rcGradRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.revenue} stopOpacity={0.22} />
              <stop offset="100%" stopColor={COLORS.revenue} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="rcGradFee" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.fee} stopOpacity={0.22} />
              <stop offset="100%" stopColor={COLORS.fee} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="rcGradSales" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.sales} stopOpacity={0.6} />
              <stop offset="100%" stopColor={COLORS.sales} stopOpacity={0.1} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#f5f5f5" />

          <XAxis
            dataKey="label"
            tick={{ fontSize: isMobile ? 10 : 12, fill: '#9ca3af', fontFamily: 'Heebo' }}
            tickFormatter={(v) => v.slice(0, 3)}
            axisLine={false}
            tickLine={false}
            dy={6}
          />
          <YAxis
            yAxisId="money"
            orientation="left"
            tickFormatter={(v) => v >= 1000 ? '$' + (v / 1000).toFixed(0) + 'k' : '$' + v.toFixed(0)}
            tick={{ fontSize: isMobile ? 10 : 11, fill: '#9ca3af', fontFamily: 'Heebo' }}
            axisLine={false}
            tickLine={false}
            width={isMobile ? 38 : 52}
          />
          <YAxis
            yAxisId="units"
            orientation="right"
            tickFormatter={(v) => v}
            tick={{ fontSize: isMobile ? 10 : 11, fill: '#9ca3af', fontFamily: 'Heebo' }}
            axisLine={false}
            tickLine={false}
            width={isMobile ? 22 : 32}
          />

          <Tooltip content={<ChartTooltip />} cursor={{ stroke: '#e5e7eb', strokeWidth: 1.5, strokeDasharray: '5 3' }} />

          {/* Legend rendered manually via content prop — recharts won't auto-fill color otherwise */}
          {/* We use a plain payload build below in the component */}

          <Area
            yAxisId="money"
            type="monotone"
            dataKey={(d) => d.totalRevenue / 100}
            name="Gross Revenue"
            stroke={COLORS.revenue}
            strokeWidth={2.5}
            fill="url(#rcGradRevenue)"
            dot={false}
            activeDot={{ r: 5, fill: COLORS.revenue, strokeWidth: 0 }}
          />
          <Area
            yAxisId="money"
            type="monotone"
            dataKey={(d) => d.platformFee / 100}
            name="Platform Fee"
            stroke={COLORS.fee}
            strokeWidth={2.5}
            fill="url(#rcGradFee)"
            dot={false}
            activeDot={{ r: 5, fill: COLORS.fee, strokeWidth: 0 }}
          />
          <Bar
            yAxisId="units"
            dataKey="totalSales"
            name="Units Sold"
            fill="url(#rcGradSales)"
            radius={[8, 8, 0, 0]}
            maxBarSize={28}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Manual legend */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, sm: 3 }, mt: 2, flexWrap: 'wrap' }}>
        {[
          { label: 'Gross Revenue', color: COLORS.revenue, isBar: false },
          { label: 'Platform Fee', color: COLORS.fee, isBar: false },
          { label: 'Units Sold', color: COLORS.sales, isBar: true },
        ].map(({ label, color, isBar }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {isBar
              ? <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: color, opacity: 0.6 }} />
              : <Box sx={{ width: 20, height: 3, borderRadius: '2px', bgcolor: color }} />
            }
            <Typography sx={{ fontSize: 12, color: '#9ca3af', fontFamily: 'Heebo' }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default RevenueChart;
